import React, { useEffect, useRef, useState } from "react";
import theMagicBuildingCurves from "../../data/theMagicBuildingCurves";

const SVG_WIDTH = 1600;
const SVG_HEIGHT = 1000;
const plot = {
  left: 225,
  right: 1388,
  top: 252,
  rowHeight: 104,
};
const rowLevels = ["High", "Medium", "Low"];
const rowLevelLabelX = plot.left - 3;
const curveTextX = 25;
const curveDescriptionMaxChars = 16;

function wrapText(text, maxChars) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines;
}

function TextBlock({
  x,
  y,
  text,
  maxChars,
  lineHeight = 18,
  className,
  maxLines = 8,
  anchor,
  style,
}) {
  const lines = wrapText(text, maxChars).slice(0, maxLines);
  return (
    <text x={x} y={y} className={className} textAnchor={anchor} style={style}>
      {lines.map((line, index) => (
        <tspan key={`${line}-${index}`} x={x} dy={index === 0 ? 0 : lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

function Icon({ type, x, y }) {
  if (type === "clock") {
    return (
      <g transform={`translate(${x} ${y})`} className="sc-icon">
        <circle cx="18" cy="18" r="15" fill="none" />
        <path d="M18 8v11l8 5" fill="none" />
      </g>
    );
  }

  if (type === "themes") {
    return (
      <g transform={`translate(${x} ${y})`} className="sc-icon">
        <path d="M4 8h28M4 18h22M4 28h32" fill="none" />
        <path d="M4 8v20" fill="none" />
      </g>
    );
  }

  return (
    <g transform={`translate(${x} ${y})`} className="sc-icon">
      <path d="M3 8c8-4 15-2 16 2v25c-4-5-10-6-16-2V8z" fill="none" />
      <path d="M35 8c-8-4-15-2-16 2v25c4-5 10-6 16-2V8z" fill="none" />
    </g>
  );
}

function columnWidth(total) {
  return (plot.right - plot.left) / Math.max(1, total);
}

function sectionCenterX(index, total) {
  return plot.left + (index + 0.5) * columnWidth(total);
}

function sectionBoundaryX(index, total) {
  return plot.left + index * columnWidth(total);
}

function pointY(rowIndex, value) {
  const rowTop = plot.top + rowIndex * plot.rowHeight;
  return rowTop + 82 - value * 7.4;
}

function sectionValues(values, sectionCount) {
  if (!Array.isArray(values)) return [];
  if (values.length === sectionCount) return values;
  if (values.length === 0 || sectionCount <= 0) return [];
  if (values.length === 1) return Array.from({ length: sectionCount }, () => values[0]);

  return Array.from({ length: sectionCount }, (_, sectionIndex) => {
    const sourceIndex = sectionIndex * ((values.length - 1) / Math.max(1, sectionCount - 1));
    const lowerIndex = Math.floor(sourceIndex);
    const upperIndex = Math.min(values.length - 1, Math.ceil(sourceIndex));
    const progress = sourceIndex - lowerIndex;
    return values[lowerIndex] + (values[upperIndex] - values[lowerIndex]) * progress;
  });
}

function curvePath(values, rowIndex, sectionCount) {
  return values
    .map((value, index) => {
      const prefix = index === 0 ? "M" : "L";
      return `${prefix}${sectionCenterX(index, sectionCount).toFixed(1)} ${pointY(rowIndex, value).toFixed(1)}`;
    })
    .join(" ");
}

function normalizeCurveId(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export default function StoryCurvesSvgChart({
  className = "",
  focusCurve = "",
  dimNonFocusCurves = false,
  curveOverrides = {},
  focusSectionIndex = null,
  animatedCurve = "",
  animationKey = 0,
  curveBadge = "",
  chartIntroState = "off",
  introVisiblePointCount = null,
}) {
  const data = theMagicBuildingCurves;
  const sectionCount = data.sections.length;
  const chartBottom = plot.top + data.curves.length * plot.rowHeight;
  const summaryTop = chartBottom + 26;
  const normalizedFocusCurve = normalizeCurveId(focusCurve);
  const normalizedAnimatedCurve = normalizeCurveId(animatedCurve);
  const isOnboardingPending = chartIntroState === "pending";
  const isOnboardingFullStep = chartIntroState === "full";
  const isOnboardingSectionStep = chartIntroState === "section";
  const isOnboardingEmotionStep = chartIntroState === "emotion";
  const isOnboardingCaptionStep = chartIntroState === "caption";
  const isOnboardingStep =
    isOnboardingFullStep ||
    isOnboardingSectionStep ||
    isOnboardingEmotionStep ||
    isOnboardingCaptionStep;
  const effectiveFocusCurve =
    (isOnboardingEmotionStep || isOnboardingCaptionStep) && !normalizedFocusCurve
      ? "emotion"
      : normalizedFocusCurve;
  const hasFocusBand =
    Number.isInteger(focusSectionIndex) &&
    focusSectionIndex >= 0 &&
    focusSectionIndex < sectionCount;
  const showFocusBand = hasFocusBand && (chartIntroState === "off" || isOnboardingStep);
  const sectionColumnWidth = columnWidth(sectionCount);
  const focusBandCenter = hasFocusBand ? sectionCenterX(focusSectionIndex, sectionCount) : 0;
  const focusBandX = hasFocusBand ? focusBandCenter - sectionColumnWidth / 2 : 0;
  const focusBandWidth = hasFocusBand ? sectionColumnWidth : 0;
  const limitedIntroPointCount =
    isOnboardingFullStep && Number.isFinite(Number(introVisiblePointCount))
      ? Math.max(0, Math.min(sectionCount, Number(introVisiblePointCount)))
      : null;
  const pathRefs = useRef([]);
  const drawPlayedRef = useRef(false);
  const rafRef = useRef([]);
  const [pathLengths, setPathLengths] = useState({});
  const [drawFrameReady, setDrawFrameReady] = useState(false);
  const [shouldDrawCurves, setShouldDrawCurves] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  useEffect(() => {
    rafRef.current.forEach((id) => window.cancelAnimationFrame(id));
    rafRef.current = [];

    if (!isOnboardingFullStep) {
      if (chartIntroState === "off" || chartIntroState === "pending") {
        drawPlayedRef.current = false;
        setDrawFrameReady(false);
        setShouldDrawCurves(false);
      } else {
        setDrawFrameReady(true);
        setShouldDrawCurves(true);
      }
      return undefined;
    }

    const lengths = {};
    pathRefs.current.forEach((node, index) => {
      if (!node) return;
      try {
        lengths[index] = Math.ceil(node.getTotalLength());
      } catch (error) {
        lengths[index] = 1000;
      }
    });
    setPathLengths(lengths);

    if (reducedMotion || drawPlayedRef.current) {
      setDrawFrameReady(true);
      setShouldDrawCurves(true);
      drawPlayedRef.current = true;
      return undefined;
    }

    setDrawFrameReady(false);
    setShouldDrawCurves(false);
    const firstFrame = window.requestAnimationFrame(() => {
      const secondFrame = window.requestAnimationFrame(() => {
        setDrawFrameReady(true);
        const thirdFrame = window.requestAnimationFrame(() => {
          drawPlayedRef.current = true;
          setShouldDrawCurves(true);
        });
        rafRef.current.push(thirdFrame);
      });
      rafRef.current.push(secondFrame);
    });
    rafRef.current.push(firstFrame);

    return () => {
      rafRef.current.forEach((id) => window.cancelAnimationFrame(id));
      rafRef.current = [];
    };
  }, [chartIntroState, isOnboardingFullStep, reducedMotion]);

  return (
    <svg
      className={`${className} ${
        isOnboardingStep ? `sc-chart-intro-${chartIntroState}` : ""
      }`.trim()}
      role="img"
      aria-labelledby="story-curves-title story-curves-desc"
      viewBox="0 0 1600 1000"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >
          <title id="story-curves-title">The Magic Building story curves</title>
          <desc id="story-curves-desc">
            A read-only visual map of six story craft curves across seven story sections.
          </desc>

          <defs>
            <radialGradient id="scBgGlow" cx="52%" cy="42%" r="72%">
              <stop offset="0%" stopColor="#102230" />
              <stop offset="48%" stopColor="#081722" />
              <stop offset="100%" stopColor="#040c13" />
            </radialGradient>
            <filter id="scGlow" x="-20%" y="-120%" width="140%" height="340%">
              <feGaussianBlur stdDeviation="10" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="scEmotionGlow" x="-30%" y="-180%" width="160%" height="460%">
              <feGaussianBlur stdDeviation="5" result="softGlow" />
              <feColorMatrix
                in="softGlow"
                type="matrix"
                values="1 0 0 0 0.96  0 0.28 0 0 0.16  0 0 0.72 0 0.38  0 0 0 0.72 0"
                result="pinkGlow"
              />
              <feMerge>
                <feMergeNode in="pinkGlow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <rect width={SVG_WIDTH} height={SVG_HEIGHT} fill="url(#scBgGlow)" />
          <rect x="16" y="16" width="1503" height="968" rx="8" fill="rgba(1,12,19,.58)" stroke="rgba(255,255,255,.08)" />

          {showFocusBand ? (
            <rect
              className={`sc-focus-band ${
                isOnboardingSectionStep ? "sc-focus-band--reveal" : ""
              }`.trim()}
              x={focusBandX}
              y={plot.top}
              width={focusBandWidth}
              height={chartBottom - plot.top}
              rx="16"
              fill="rgba(244, 114, 182, 0.16)"
              stroke="rgba(244, 114, 182, 0.48)"
            />
          ) : null}

          <g className="sc-title-block">
            <text x="26" y="55" className="sc-title">
              {data.title}
            </text>
            <text x="27" y="88" className="sc-subtitle">
              {data.subtitle}
            </text>
            <TextBlock
              x={27}
              y={118}
              text={data.description}
              maxChars={52}
              lineHeight={23}
              className="sc-intro"
              maxLines={3}
            />
          </g>

          <g className="sc-glance">
            <rect x="464" y="19" width="610" height="125" rx="5" />
            <text x="769" y="43" className="sc-card-heading" textAnchor="middle">
              STORY AT A GLANCE
            </text>
            {data.glance.map((item, index) => {
              const x = 486 + index * 185;
              const iconX = x;
              return (
                <g key={item.label}>
                  <Icon type={item.icon} x={iconX} y="68" />
                  <text x={x + 48} y="81" className="sc-glance-label">
                    {item.label}
                  </text>
                  <TextBlock
                    x={x + 48}
                    y={106}
                    text={item.value}
                    maxChars={index === 2 ? 24 : 18}
                    lineHeight={20}
                    className="sc-glance-value"
                    maxLines={index === 1 ? 1 : 2}
                  />
                </g>
              );
            })}
          </g>

          <g className="sc-read-card">
            <rect x="1102" y="19" width="333" height="125" rx="5" />
            <text x="1268" y="50" className="sc-card-heading" textAnchor="middle">
              HOW TO READ THE CURVES
            </text>
            <line x1="1126" y1="81" x2="1178" y2="81" className="sc-key-high" />
            <line x1="1126" y1="110" x2="1178" y2="110" className="sc-key-low" />
            <text x="1190" y="87" className="sc-key-text">High</text>
            <text x="1190" y="116" className="sc-key-text">Low</text>
            <line x1="1248" y1="69" x2="1248" y2="130" className="sc-card-rule" />
            <TextBlock
              x={1268}
              y={86}
              text="Each curve shows the intensity of an element through the story."
              maxChars={25}
              lineHeight={20}
              className="sc-glance-value"
              maxLines={3}
            />
          </g>

          {data.sections.map((section, index) => {
            const gridX = sectionBoundaryX(index, sectionCount);
            const labelX = sectionCenterX(index, sectionCount);
            return (
              <g key={section}>
                <line x1={gridX} y1="169" x2={gridX} y2={summaryTop + 66} className="sc-section-line" />
                <TextBlock
                  x={labelX}
                  y="176"
                  text={section}
                  maxChars={16}
                  lineHeight={18}
                  className="sc-section-label"
                  maxLines={4}
                  anchor="middle"
                />
              </g>
            );
          })}

          {showFocusBand ? (
            <text
              className={`sc-focus-band-label ${
                isOnboardingSectionStep ? "sc-focus-band-label--reveal" : ""
              }`.trim()}
              x={focusBandCenter}
              y={plot.top - 24}
              textAnchor="middle"
            >
              Section 5 focus
            </text>
          ) : null}

          {normalizedAnimatedCurve ? (
            <g
              key={`curve-badge-${animationKey}-${curveBadge}`}
              className={`sc-curve-badge ${animationKey > 0 ? "sc-curve-badge--active" : ""}`}
            >
              <rect
                x={focusBandCenter - 82}
                y={plot.top - 72}
                width="164"
                height="30"
                rx="15"
              />
              <text x={focusBandCenter} y={plot.top - 51} textAnchor="middle">
                {curveBadge || "Emotion"}
              </text>
            </g>
          ) : null}

          {data.curves.map((curve, rowIndex) => {
            const curveId = normalizeCurveId(curve.label);
            const overrideValues = curveOverrides?.[curveId];
            const rawCurveValues = Array.isArray(overrideValues) ? overrideValues : curve.values;
            const curveValues = sectionValues(rawCurveValues, sectionCount);
            const visibleCurveValues =
              limitedIntroPointCount == null
                ? curveValues
                : limitedIntroPointCount <= 0
                ? []
                : curveValues;
            const rowTop = plot.top + rowIndex * plot.rowHeight;
            const isIntroFocusCurve = Boolean(
              (isOnboardingEmotionStep || isOnboardingCaptionStep) && curveId === "emotion"
            );
            const isDimmed = Boolean(
              (dimNonFocusCurves && effectiveFocusCurve && curveId !== effectiveFocusCurve) ||
                ((isOnboardingEmotionStep || isOnboardingCaptionStep) && curveId !== "emotion")
            );
            const curveOpacity = isDimmed ? 0.42 : 1;
            const labelOpacity = isDimmed ? 0.56 : 1;
            const displayedCurveOpacity = isOnboardingPending ? 0 : curveOpacity;
            const displayedLabelOpacity = isOnboardingPending ? 0 : labelOpacity;
            const strokeWidth = isIntroFocusCurve ? 4 : isDimmed ? 1.8 : 3;
            const pointRadius = isDimmed ? 3.5 : 4.4;
            const isAnimatedCurve = Boolean(normalizedAnimatedCurve && curveId === normalizedAnimatedCurve);
            const shouldDrawAnimate = isAnimatedCurve && animationKey > 0;
            const shouldIntroDraw = isOnboardingFullStep;
            const shouldEmotionPulse = isOnboardingEmotionStep && curveId === "emotion";
            const pathLength = pathLengths[rowIndex] || 1000;
            const curveDelay = `${rowIndex * 70}ms`;
            const dotDelay = `${900 + rowIndex * 70}ms`;
            const legendDelay = `${140 + rowIndex * 55}ms`;
            const introReady = drawFrameReady || reducedMotion;
            const introDrawComplete =
              shouldDrawCurves ||
              reducedMotion ||
              (limitedIntroPointCount != null && limitedIntroPointCount >= sectionCount);
            const introPathStyle = shouldIntroDraw
              ? {
                  opacity: introDrawComplete ? displayedCurveOpacity : 0,
                  strokeDasharray: pathLength,
                  strokeDashoffset: introDrawComplete ? 0 : pathLength,
                  transition: reducedMotion
                    ? "none"
                    : introReady
                    ? `stroke-dashoffset 1280ms cubic-bezier(.22,.78,.18,1) ${curveDelay}, opacity 320ms ease ${curveDelay}`
                    : "none",
                }
              : undefined;
            const introLegendStyle = shouldIntroDraw
              ? {
                  opacity: introDrawComplete ? displayedLabelOpacity : 0,
                  transition:
                    reducedMotion || !introReady ? "none" : `opacity 520ms ease ${legendDelay}`,
                }
              : undefined;
            const introDotStyle = shouldIntroDraw
              ? {
                  opacity: introDrawComplete ? displayedCurveOpacity : 0,
                  transform: introDrawComplete ? "scale(1)" : "scale(.72)",
                  transformBox: "fill-box",
                  transformOrigin: "center",
                  transition: reducedMotion
                    ? "none"
                    : !introReady
                    ? "none"
                    : `opacity 420ms ease ${dotDelay}, transform 420ms ease ${dotDelay}`,
                }
              : undefined;

            return (
              <g key={curve.label}>
                <line x1={plot.left} y1={rowTop + 2} x2={plot.right} y2={rowTop + 2} className="sc-row-line" />
                <line x1={plot.left} y1={rowTop + 40} x2={plot.right} y2={rowTop + 40} className="sc-row-line sc-row-line-soft" />
                <line x1={plot.left} y1={rowTop + 82} x2={plot.right} y2={rowTop + 82} className="sc-row-line" />
                <text
                  x={curveTextX}
                  y={rowTop + 34}
                  fill={curve.color}
                  className={`sc-curve-label ${
                    shouldIntroDraw ? "sc-curve-legend--intro" : ""
                  }`.trim()}
                  opacity={displayedLabelOpacity}
                  style={introLegendStyle}
                >
                  {curve.label.toUpperCase()}
                </text>
                <TextBlock
                  x={curveTextX}
                  y={rowTop + 56}
                  text={curve.description}
                  maxChars={curveDescriptionMaxChars}
                  lineHeight={18}
                  className={`sc-curve-description ${isDimmed ? "sc-muted-copy" : ""} ${
                    shouldIntroDraw ? "sc-curve-legend--intro" : ""
                  } ${isOnboardingPending ? "sc-hidden-copy" : ""}`}
                  maxLines={3}
                  style={introLegendStyle}
                />
                {rowLevels.map((level, index) => (
                  <text key={level} x={rowLevelLabelX} y={rowTop + 14 + index * 34} className="sc-level-label">
                    {level}
                  </text>
                ))}
                <path
                  ref={(node) => {
                    pathRefs.current[rowIndex] = node;
                  }}
                  key={`${curve.label}-path-${isAnimatedCurve ? animationKey : 0}-${
                    shouldIntroDraw ? "intro-full" : "static"
                  }`}
                  className={`sc-curve-path ${
                    shouldDrawAnimate ? "sc-curve-path--draw" : ""
                  } ${shouldIntroDraw ? "sc-curve-path--intro-draw" : ""} ${
                    shouldEmotionPulse ? "sc-curve-path--emotion-pulse" : ""
                  }`.trim()}
                  d={curvePath(visibleCurveValues, rowIndex, sectionCount)}
                  fill="none"
                  stroke={curve.color}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  filter={isAnimatedCurve || isIntroFocusCurve ? "url(#scEmotionGlow)" : "url(#scGlow)"}
                  opacity={displayedCurveOpacity}
                  pathLength={pathLength}
                  style={introPathStyle}
                />
                {(shouldIntroDraw && !introDrawComplete ? [] : visibleCurveValues).map((value, index) => (
                  <circle
                    key={`${curve.label}-${index}-${isAnimatedCurve ? animationKey : 0}`}
                    className={`${
                      shouldDrawAnimate ? "sc-curve-dot--focus" : ""
                    } ${shouldIntroDraw ? "sc-curve-dot--intro" : ""}`.trim()}
                    cx={sectionCenterX(index, sectionCount)}
                    cy={pointY(rowIndex, value)}
                    r={pointRadius}
                    fill={curve.color}
                    stroke={curve.color}
                    opacity={displayedCurveOpacity}
                    style={introDotStyle}
                  />
                ))}
              </g>
            );
          })}

          {isOnboardingCaptionStep ? (
            <g className="sc-intro-caption sc-intro-caption--active">
              <text x="768" y="916" textAnchor="middle">
                Stories create different reader experiences.
              </text>
              <text x="768" y="943" textAnchor="middle">
                In this lesson, you'll change the Emotion curve by rewriting Section 5.
              </text>
            </g>
          ) : null}

          <text x="25" y={summaryTop + 48} className="sc-bottom-label">
            WHAT HAPPENS
          </text>
          <text x="25" y={summaryTop + 72} className="sc-bottom-label">
            IN THIS PART
          </text>
          {data.summaries.map((summary, index) => {
            const x = sectionCenterX(index, sectionCount);
            return (
              <TextBlock
                key={summary}
                x={x}
                y={summaryTop + 8}
                text={summary}
                maxChars={15}
                lineHeight={16}
                className="sc-summary"
                maxLines={5}
                anchor="middle"
              />
            );
          })}

          {data.notes.map((note, index) => (
            <g key={note.title}>
              <TextBlock
                x="1412"
                y={plot.top + index * plot.rowHeight + 34}
                text={note.text}
                maxChars={17}
                lineHeight={17}
                className="sc-note"
                maxLines={5}
              />
            </g>
          ))}

          <text x="768" y="986" textAnchor="middle" className="sc-footer">
            {data.footerNote}
          </text>

          <style>
            {`
              .sc-title { fill: #f2eadf; font: 700 39px Georgia, serif; }
              .sc-subtitle { fill: #f1eadf; font: 20px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
              .sc-intro, .sc-glance-value, .sc-key-text, .sc-note, .sc-summary, .sc-curve-description { fill: #f0ece2; font: 15px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
              .sc-muted-copy { opacity: .56; }
              .sc-hidden-copy { opacity: 0; }
              .sc-glance rect, .sc-read-card rect { fill: rgba(3, 16, 25, .76); stroke: rgba(255, 255, 255, .16); }
              .sc-card-heading { fill: #f5ecdf; font: 700 17px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; letter-spacing: 4px; }
              .sc-icon { stroke: #f2eadf; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; }
              .sc-glance-label { fill: #f4efe7; font: 16px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
              .sc-card-rule, .sc-row-line { stroke: rgba(255, 255, 255, .12); }
              .sc-row-line-soft { stroke: rgba(255, 255, 255, .07); }
              .sc-section-line { stroke: rgba(255, 255, 255, .2); stroke-dasharray: 4 4; }
              .sc-section-label { fill: #f5eee5; font: 16px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
              .sc-curve-label, .sc-bottom-label { font: 700 16px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; letter-spacing: .5px; }
              .sc-bottom-label { fill: #facc15; }
              .sc-level-label { fill: #f5eee5; font: 15px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; text-anchor: end; }
              .sc-focus-band-label { fill: #f9a8d4; font: 700 15px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
              .sc-chart-intro-full {
                opacity: 0;
                animation: scChartEnter 520ms ease-out forwards;
              }
              .sc-curve-label, .sc-curve-description {
                transition: opacity 650ms ease;
              }
              .sc-curve-path {
                transition: opacity 650ms ease, stroke-width 650ms ease, filter 650ms ease;
              }
              .sc-curve-path--intro-draw {
                stroke-dasharray: 1000;
                stroke-dashoffset: 1000;
              }
              .sc-curve-path--draw {
                stroke-dasharray: 1000;
                stroke-dashoffset: 1000;
                animation:
                  scLineDraw 1100ms cubic-bezier(.22,.78,.18,1) forwards,
                  scFocusPulse 820ms ease-out;
              }
              .sc-curve-path--emotion-pulse {
                animation: scEmotionFocusPulse 760ms ease-out;
              }
              .sc-curve-dot--focus {
                animation: scDotFade 520ms ease-out both;
              }
              .sc-curve-dot--intro {
                opacity: 0;
                transform-box: fill-box;
                transform-origin: center;
              }
              .sc-curve-legend--intro {
                opacity: 0;
              }
              .sc-focus-band--reveal {
                opacity: 0;
                transform: translateY(12px);
                animation: scBandReveal 680ms ease-out forwards;
              }
              .sc-focus-band-label--reveal {
                opacity: 0;
                transform: translateY(8px);
                animation: scBandReveal 620ms ease-out 140ms forwards;
              }
              .sc-intro-caption {
                opacity: 0;
              }
              .sc-intro-caption text {
                fill: #f5eee5;
                font: 700 18px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
              }
              .sc-intro-caption--active {
                animation: scCaptionFade 620ms ease-out forwards;
              }
              .sc-curve-badge rect {
                fill: rgba(8, 17, 26, .9);
                stroke: rgba(249, 168, 212, .54);
              }
              .sc-curve-badge text {
                fill: #f9a8d4;
                font: 800 14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
                letter-spacing: .4px;
              }
              .sc-curve-badge--active {
                animation: scBadgeFade 360ms ease-out both;
              }
              @keyframes scLineDraw {
                to { stroke-dashoffset: 0; }
              }
              @keyframes scChartEnter {
                from { opacity: 0; transform: translateY(8px); }
                to { opacity: 1; transform: translateY(0); }
              }
              @keyframes scDotIntro {
                0% { opacity: 0; transform: scale(.72); }
                100% { opacity: 1; transform: scale(1); }
              }
              @keyframes scLegendIntro {
                to { opacity: 1; }
              }
              @keyframes scBandReveal {
                to { opacity: 1; transform: translateY(0); }
              }
              @keyframes scCaptionFade {
                to { opacity: 1; }
              }
              @keyframes scEmotionGlowPulse {
                0% { opacity: .82; }
                100% { opacity: 1; }
              }
              @keyframes scEmotionFocusPulse {
                0% { filter: url(#scEmotionGlow); stroke-width: 4; }
                45% { filter: url(#scEmotionGlow); stroke-width: 5; }
                100% { filter: url(#scEmotionGlow); stroke-width: 4; }
              }
              @keyframes scFocusPulse {
                0% { opacity: .5; }
                40% { opacity: 1; }
                100% { opacity: 1; }
              }
              @keyframes scDotFade {
                0%, 58% { opacity: 0; }
                100% { opacity: 1; }
              }
              @keyframes scBadgeFade {
                from { opacity: 0; transform: translateY(6px); }
                to { opacity: 1; transform: translateY(0); }
              }
              @media (prefers-reduced-motion: reduce) {
                .sc-curve-path,
                .sc-curve-path--draw,
                .sc-curve-path--intro-draw,
                .sc-curve-path--emotion-pulse,
                .sc-curve-dot--focus,
                .sc-curve-dot--intro,
                .sc-curve-badge--active,
                .sc-curve-legend--intro,
                .sc-focus-band--reveal,
                .sc-focus-band-label--reveal,
                .sc-intro-caption--active,
                .sc-chart-intro-full {
                  animation: none;
                  transition: none;
                }
                .sc-curve-path--draw, .sc-curve-path--intro-draw {
                  stroke-dashoffset: 0;
                }
                .sc-curve-dot--intro,
                .sc-curve-legend--intro,
                .sc-focus-band--reveal,
                .sc-focus-band-label--reveal,
                .sc-intro-caption--active,
                .sc-chart-intro-full {
                  opacity: 1;
                  transform: none;
                }
              }
              .sc-key-high { stroke: #f2eadf; stroke-width: 2; }
              .sc-key-low { stroke: #f2eadf; stroke-width: 1.5; stroke-dasharray: 7 8; }
              .sc-footer { fill: rgba(245, 238, 229, .72); font: italic 15px Georgia, serif; }
            `}
          </style>
    </svg>
  );
}
